import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeRaw from 'rehype-raw';
import { ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';

// 애니메이션 variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: 'easeOut',
    },
  },
};

// Parse frontmatter (---...---) and custom markdown sections
const parseMarkdownFile = (text) => {
  // Remove BOM and normalize line endings
  const normalized = text.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n');
  
  const frontmatterRegex = /^---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/;
  const match = normalized.match(frontmatterRegex);
  
  if (!match) {
    return { meta: {}, content: normalized };
  }
  
  const frontmatter = match[1];
  const content = match[2];
  
  const meta = {};
  frontmatter.split('\n').forEach(line => {
    const colonIndex = line.indexOf(':');
    if (colonIndex > -1) {
      const key = line.substring(0, colonIndex).trim();
      const value = line.substring(colonIndex + 1).trim();
      if (key) {
        meta[key] = value;
      }
    }
  });
  
  return { meta, content };
};

// Parse headings for table of contents
const parseHeadings = (markdown) => {
  const headings = [];
  const lines = markdown.split('\n');

  lines.forEach((line) => {
    const match = line.match(/^(#{1,3})\s+(.+)$/);
    if (match) {
      const level = match[1].length;
      const text = match[2].trim();
      const id = text.toLowerCase().replace(/[^a-z0-9가-힣]+/g, '-').replace(/(^-|-$)/g, '');
      headings.push({ level, text, id });
    }
  });

  return headings;
};

// Custom markdown parser for :::left and :::right sections
const parseCustomMarkdown = (markdown) => {
  const sections = [];
  const lines = markdown.split('\n');
  let currentSection = null;
  let inCustomBlock = false;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    
    if (trimmed === ':::left') {
      // Save previous section if exists
      if (currentSection && currentSection.content.length > 0) {
        sections.push(currentSection);
      }
      currentSection = { type: 'left', content: [] };
      inCustomBlock = true;
    } else if (trimmed === ':::right') {
      // Save previous section if exists
      if (currentSection && currentSection.content.length > 0) {
        sections.push(currentSection);
      }
      currentSection = { type: 'right', content: [] };
      inCustomBlock = true;
    } else if (trimmed === ':::' && inCustomBlock) {
      // End of custom block
      if (currentSection && currentSection.content.length > 0) {
        sections.push(currentSection);
      }
      currentSection = null;
      inCustomBlock = false;
    } else if (inCustomBlock) {
      // Add line to current custom block
      if (currentSection) {
        currentSection.content.push(line);
      }
    } else {
      // Normal content outside custom blocks
      if (!currentSection || currentSection.type !== 'normal') {
        if (currentSection && currentSection.content.length > 0) {
          sections.push(currentSection);
        }
        currentSection = { type: 'normal', content: [] };
      }
      // Only add non-empty lines or lines that are part of content
      if (trimmed !== '' || (currentSection.content.length > 0 && currentSection.content[currentSection.content.length - 1] !== '')) {
        currentSection.content.push(line);
      }
    }
  }
  
  // Push remaining content
  if (currentSection && currentSection.content.length > 0) {
    sections.push(currentSection);
  }
  
  return sections;
};

const ProjectDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeId, setActiveId] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // 스크롤 감지하여 현재 읽고 있는 섹션 하이라이트
  useEffect(() => {
    const handleScroll = () => {
      const headingElements = Array.from(document.querySelectorAll('h1[id], h2[id], h3[id]'));
      if (headingElements.length === 0) return;

      let currentId = headingElements[0].id;
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;

      // 페이지 하단 근처면 마지막 헤딩 선택
      if (scrollY + windowHeight >= docHeight - 100) {
        setActiveId(headingElements[headingElements.length - 1].id);
        return;
      }

      // 화면 상단을 지나간 헤딩 중 가장 마지막 것 선택
      for (const el of headingElements) {
        const rect = el.getBoundingClientRect();
        if (rect.top <= 200) {
          currentId = el.id;
        }
      }

      setActiveId(currentId);
    };

    window.addEventListener('scroll', handleScroll);
    setTimeout(handleScroll, 200);

    return () => window.removeEventListener('scroll', handleScroll);
  }, [data]);

  useEffect(() => {
    const fetchMarkdown = async () => {
      try {
        const response = await fetch(`/projects/${slug}.md`);
        if (!response.ok) {
          throw new Error('프로젝트를 찾을 수 없습니다.');
        }
        const text = await response.text();
        const { meta, content } = parseMarkdownFile(text);
        setData({ ...meta, markdown: content });
        setLoading(false);
      } catch (err) {
        console.error('Fetch error:', err);
        setError(err.message);
        setLoading(false);
      }
    };
    
    fetchMarkdown();
  }, [slug]);

  const handleBack = () => {
    navigate('/', { state: { scrollTo: 'projects' } });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 py-12">
        <div className="max-w-5xl mx-auto px-4">
          <p className="text-neutral-600">로딩 중...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-neutral-50 py-12">
        <div className="max-w-5xl mx-auto px-4">
          <button
            onClick={handleBack}
            className="flex items-center text-neutral-700 hover:text-primary-600 transition-colors mb-8 group"
          >
            <ArrowLeft className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" />
            <span className="font-medium">뒤로가기</span>
          </button>
          <p className="text-neutral-700">{error || '프로젝트를 찾을 수 없습니다.'}</p>
        </div>
      </div>
    );
  }

  const sections = parseCustomMarkdown(data.markdown || '');
  const headings = parseHeadings(data.markdown || '');
  
  // Group left/right pairs
  const groupedSections = [];
  let i = 0;
  while (i < sections.length) {
    const current = sections[i];
    const next = sections[i + 1];
    
    if ((current.type === 'left' && next?.type === 'right') || 
        (current.type === 'right' && next?.type === 'left')) {
      groupedSections.push({ 
        type: 'pair', 
        left: current.type === 'left' ? current : next, 
        right: current.type === 'right' ? current : next 
      });
      i += 2;
    } else {
      groupedSections.push(current);
      i += 1;
    }
  }

  // Generate id from heading text
  const generateId = (text) => {
    return text.toLowerCase().replace(/[^a-z0-9가-힣]+/g, '-').replace(/(^-|-$)/g, '');
  };

  // Extract text from React children
  const getTextFromChildren = (children) => {
    if (typeof children === 'string') return children;
    if (Array.isArray(children)) {
      return children.map(getTextFromChildren).join('');
    }
    if (children?.props?.children) {
      return getTextFromChildren(children.props.children);
    }
    return '';
  };

  // Common markdown renderer enhancements (images, links, code)
  const markdownComponents = {
    img: ({ node, ...props }) => (
      <img
        {...props}
        className="rounded-lg border shadow-sm mx-auto max-h-80 object-contain"
        loading="lazy"
        alt={props.alt || ''}
      />
    ),
    a: ({ node, ...props }) => (
      <a {...props} className="text-primary-600 underline underline-offset-2 hover:text-primary-700" />
    ),
    h1: ({ node, children, ...props }) => (
      <h1 id={generateId(getTextFromChildren(children))} {...props} className="!text-3xl font-bold tracking-tight text-neutral-900 !mt-6 !mb-3">
        {children}
      </h1>
    ),
    h2: ({ node, children, ...props }) => (
      <h2 id={generateId(getTextFromChildren(children))} {...props} className="!text-2xl font-bold tracking-tight text-neutral-900 !mt-5 !mb-2">
        {children}
      </h2>
    ),
    h3: ({ node, children, ...props }) => (
      <h3 id={generateId(getTextFromChildren(children))} {...props} className="!text-xl font-semibold tracking-tight text-neutral-800 !mt-4 !mb-2">
        {children}
      </h3>
    ),
    ul: ({ node, ...props }) => (
      <ul {...props} className="!list-disc !pl-5 !my-2" />
    ),
    ol: ({ node, ...props }) => (
      <ol {...props} className="!list-decimal !pl-5 !my-2" />
    ),
    li: ({ node, ...props }) => (
      <li {...props} className="!my-0.5" />
    ),
    p: ({ node, ...props }) => (
      <p {...props} className="!my-4" />
    ),
    table: ({ node, ...props }) => (
      <div className="overflow-x-auto w-full">
        <table {...props} className="w-full table-auto border-collapse" />
      </div>
    ),
  };

  return (
    <div className="min-h-screen bg-neutral-50 pt-24 md:pt-32 pb-10 md:pb-14 relative">
      {/* 오른쪽 목차 네비게이션 - 데스크탑만 */}
      {headings.length > 0 && (
        <nav className="hidden xl:block fixed right-8 top-32 w-56">
          <p className="text-xs font-bold text-neutral-400 uppercase tracking-wider mb-3">목차</p>
          <ul className="space-y-1.5 border-l-2 border-neutral-200">
            {headings.map((heading, idx) => (
              <li key={idx} className="relative">
                {activeId === heading.id && (
                  <span className="absolute left-0 top-0 bottom-0 w-0.5 bg-primary-600 -ml-0.5" />
                )}
                <a
                  href={`#${heading.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById(heading.id)?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className={`block text-sm font-bold transition-colors ${
                    heading.level === 1 ? 'pl-3' : heading.level === 2 ? 'pl-5' : 'pl-7'
                  } ${
                    activeId === heading.id
                      ? 'text-primary-600'
                      : 'text-neutral-500 hover:text-primary-600'
                  }`}
                >
                  {heading.text}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      )}

      <motion.div
        className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* 뒤로가기 버튼 - 콘텐츠 영역 바깥 왼쪽, 제목과 같은 높이 */}
        <button
          onClick={handleBack}
          className="absolute -left-32 top-2 md:top-3 flex items-center gap-1 text-neutral-500 hover:text-primary-600 transition-colors hidden lg:flex cursor-pointer"
          aria-label="뒤로가기"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm font-bold">뒤로가기</span>
        </button>

        <motion.div variants={itemVariants} className="mb-4 md:mb-6 flex items-center gap-3">
          {/* 모바일/태블릿용 뒤로가기 */}
          <button
            onClick={handleBack}
            className="flex items-center gap-1 text-neutral-500 hover:text-primary-600 transition-colors lg:hidden cursor-pointer"
            aria-label="뒤로가기"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-bold">뒤로가기</span>
          </button>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-neutral-900">
            {data.title || 'Untitled'}
          </h1>
        </motion.div>

        <motion.div variants={itemVariants} className="mb-12">
          <p className="text-lg text-neutral-600">{data.summary || ''}</p>

          {(data.startDate || data.endDate || data.completionRate || data.status) && (
            <div className="pt-6">
              <table className="w-full">
                <tbody>
                  {data.startDate && (
                    <tr className="border-b border-neutral-200">
                      <td className="py-2 pr-4 text-neutral-600 font-medium text-sm w-28">시작일</td>
                      <td className="py-2 text-neutral-800 text-sm">{data.startDate}</td>
                    </tr>
                  )}
                  {data.endDate && (
                    <tr className="border-b border-neutral-200">
                      <td className="py-2 pr-4 text-neutral-600 font-medium text-sm">종료일</td>
                      <td className="py-2 text-neutral-800 text-sm">{data.endDate}</td>
                    </tr>
                  )}
                  {data.completionRate && (
                    <tr className="border-b border-neutral-200">
                      <td className="py-2 pr-4 text-neutral-600 font-medium text-sm">완료율</td>
                      <td className="py-2">
                        <div className="flex items-center gap-3">
                          <div className="flex-1 h-2 bg-neutral-200 rounded-full overflow-hidden max-w-xs">
                            <div 
                              className="h-full bg-primary-600 rounded-full transition-all duration-500"
                              style={{ width: data.completionRate }}
                            />
                          </div>
                          <span className="text-neutral-800 font-semibold text-sm">{data.completionRate}</span>
                        </div>
                      </td>
                    </tr>
                  )}
                  {(data.visibility !== undefined || data.public !== undefined) && (
                    <tr className="border-b border-neutral-200">
                      <td className="py-2 pr-4 text-neutral-600 font-medium text-sm">공개 상태</td>
                      <td className="py-2 text-neutral-800 text-sm">
                        {typeof data.visibility === 'string'
                          ? data.visibility
                          : typeof data.public === 'string'
                          ? data.public
                          : (data.visibility ?? data.public) ? '공개' : '비공개'}
                      </td>
                    </tr>
                  )}
                  {data.status && (
                    <tr>
                      <td className="py-2 pr-4 text-neutral-600 font-medium text-sm">상태</td>
                      <td className="py-2">
                        <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium">
                          {data.status}
                        </span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </motion.div>

        {/* Markdown content - use Contact section background (bg-neutral-50), full width */}
        <div className="mt-10 md:mt-14 w-full space-y-1 md:space-y-2">
          {groupedSections.map((section, idx) => {
            if (section.type === 'pair') {
              return (
                <motion.div key={idx} variants={itemVariants} className="grid md:grid-cols-2 gap-8 md:gap-12 items-start">
                  <div className="prose prose-neutral prose-sm !max-w-none [&>*:first-child]:!mt-0">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm, remarkBreaks]}
                      rehypePlugins={[rehypeRaw]}
                      components={markdownComponents}
                    >
                      {section.left.content.join('\n')}
                    </ReactMarkdown>
                  </div>
                  <div className="prose prose-neutral prose-sm !max-w-none [&>*:first-child]:!mt-0">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm, remarkBreaks]}
                      rehypePlugins={[rehypeRaw]}
                      components={markdownComponents}
                    >
                      {section.right.content.join('\n')}
                    </ReactMarkdown>
                  </div>
                </motion.div>
              );
            } else {
              return (
                <motion.div key={idx} variants={itemVariants} className="prose prose-neutral prose-sm !max-w-none prose-p:my-2 prose-li:my-1 prose-headings:mb-2 prose-headings:mt-4">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm, remarkBreaks]}
                    rehypePlugins={[rehypeRaw]}
                    components={markdownComponents}
                  >
                    {section.content.join('\n')}
                  </ReactMarkdown>
                </motion.div>
              );
            }
          })}
        </div>
      </motion.div>
    </div>
  );
};

export default ProjectDetail;
